import { Accordion, AccordionDetails, AccordionSummary, TableCell, TableRow } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DotLegalCheckbox, DotLegalHeader, DotLegalMultiSelect, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { ITableHeader } from "@dotlegal/dotlegal-ui-components/dist/cjs/types/components/dotLegalTable/DotLegalTable.types";
import DotLegalTableWithControlsLocalized from "../../../common/components/dotLegalTableWithControlsLocalized/DotLegalTableWithControlsLocalized";
import { useTranslation } from "@localization/useTranslation";
import { FieldPermissionRow, useFieldPermissions } from "./FieldPermissions.hooks";
import { getEntityTemplateTranslationKeys, TemplateEntityType } from "../../../entityTemplates/EntityTemplate.types.ts";

export interface FieldPermissionsProps {
    roleId: string;
    readOnly: boolean;
    expanded: boolean;
    onExpandToggle: () => void;
    templateEntityType: TemplateEntityType;
}

function FieldPermissions(props: FieldPermissionsProps) {
    const { translateString } = useTranslation();
    const {
        isLoading,
        fields,
        isHidden,
        isRead,
        isEdit,
        isFieldDisabled,
        isFeatureGated,
        getFeatureGatedTooltip,
        toggleRead,
        toggleEdit,
        onSelectAllEditChanged,
        headerCheckBoxState,
        getSubPermissionOptions,
        getChosenSubPermissions,
        setSubPermissions,
        isMasterReadOnly,
    } = useFieldPermissions(props);

    function getHeaders(): ITableHeader<FieldPermissionRow>[] {
        return [
            { property: "name", text: translateString("name"), align: "left", showOnMobile: true },
            { property: null, text: translateString("read"), align: "center", notSortable: true },
            { property: null, text: translateString("edit"), align: "center", notSortable: true },
            { property: null, text: translateString("subPermissions"), align: "center", notSortable: true, width: "350px" },
        ];
    }

    if (fields.length === 0 && !isLoading) {
        return null;
    }

    return (
        <Accordion expanded={props.expanded} onChange={props.onExpandToggle}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <DotLegalHeader marginBottom={0} headerStyle={"extraSmall"}>
                    {translateString(getEntityTemplateTranslationKeys(props.templateEntityType).inputFields)}
                </DotLegalHeader>
            </AccordionSummary>
            <AccordionDetails>
                <DotLegalTableWithControlsLocalized
                    data={fields}
                    headers={getHeaders()}
                    defaultOrderBy={"name"}
                    defaultOrder={"asc"}
                    isLoading={isLoading}
                    clickableRows={false}
                    headerCheckBoxState={headerCheckBoxState}
                    onSelectAllChanged={onSelectAllEditChanged}
                    disableCheckAll={isMasterReadOnly}
                    hidePagination
                    getUserSpecificPageLength={() => undefined}
                    renderRow={(row) => {
                        const disabled = isFieldDisabled(row);
                        const hidden = isHidden(row);
                        const readChecked = isRead(row);
                        const editChecked = isEdit(row);
                        const featureGated = isFeatureGated(row);
                        const subPermOptions = getSubPermissionOptions(row);

                        return (
                            <DotLegalTooltip text={getFeatureGatedTooltip(row)} key={row.key}>
                                <TableRow sx={{ opacity: featureGated ? 0.5 : 1 }}>
                                    <TableCell>
                                        <DotLegalCheckbox
                                            margin={{ marginLeft: -1.5 }}
                                            checked={!hidden}
                                            disabled={disabled}
                                            onChange={() => toggleRead(row)}
                                        />
                                    </TableCell>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell align="center">
                                        <DotLegalCheckbox checked={readChecked} disabled onChange={() => toggleRead(row)} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <DotLegalCheckbox
                                            checked={editChecked}
                                            disabled={disabled || hidden || isMasterReadOnly}
                                            onChange={() => toggleEdit(row)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {subPermOptions.length > 0 && (
                                            <DotLegalMultiSelect
                                                disabled={disabled || hidden || isMasterReadOnly}
                                                options={subPermOptions}
                                                chosenOptions={getChosenSubPermissions(row)}
                                                onChange={(values) => setSubPermissions(row, values)}
                                                placeholder={translateString("subPermissions")}
                                                label={""}
                                                noOptionsLabel={translateString("noOptions")}
                                                limit={1}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            </DotLegalTooltip>
                        );
                    }}
                />
            </AccordionDetails>
        </Accordion>
    );
}

export default FieldPermissions;
